import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ActionCable from "actioncable";
import { CONTENT_GROUP_ACTION } from "../../constants";

function MinimizedCardActionCable() {
    const dispatch = useDispatch();
    const { id: userId } = useSelector((s) => s.dashboard.currentUser || {});
    const cableUrl = process.env.REACT_APP_CABLE_URL;

    const cableRef = useRef(null);
    const subscriptionRef = useRef(null);

    // Lazily create the ActionCable consumer and clean up on unmount
    useEffect(() => {
        if (!cableUrl) {
            console.warn("REACT_APP_CABLE_URL is not set; ActionCable will not connect.");
            return;
        }

        if (!cableRef.current) {
            cableRef.current = ActionCable.createConsumer(cableUrl);
        }

        return () => {
            // disconnect the consumer when the component unmounts
            if (cableRef.current?.disconnect) cableRef.current.disconnect();
            cableRef.current = null;
        };
    }, [cableUrl]);

    const handleReceivedMinimizedCards = useCallback(
        (res) => {
            const type = res?.data?.type;
            switch (type) {
                case "bulk_destroy_minimized_cards":
                    dispatch({
                        type: CONTENT_GROUP_ACTION.BULK_REMOVE_MINIMIZED_CARDS,
                        payload: res.data.data || [],
                    });
                    break;
                default:
                    console.error("Invalid minimized card broadcast type:", type);
            }
        },
        [dispatch]
    );

    // Subscribe/unsubscribe when userId changes
    useEffect(() => {
        // unsubscribe previous subscription if any
        if (subscriptionRef.current && cableRef.current?.subscriptions) {
            try {
                cableRef.current.subscriptions.remove(subscriptionRef.current);
            } catch (err) {
                console.warn("Failed to remove previous subscription", err);
            }
            subscriptionRef.current = null;
        }

        if (!userId || !cableRef.current) return;

        const channel = { channel: "MinimizedCardChannel", user_id: userId };

        subscriptionRef.current = cableRef.current.subscriptions.create(channel, {
            received: handleReceivedMinimizedCards,
            connected: () => console.log("Connected to MinimizedCardChannel"),
            disconnected: () => console.log("Disconnected from MinimizedCardChannel"),
        });

        console.log("Subscribed to MinimizedCardChannel for user:", userId);

        return () => {
            if (subscriptionRef.current && cableRef.current?.subscriptions) {
                try {
                    cableRef.current.subscriptions.remove(subscriptionRef.current);
                    console.log("Unsubscribed from MinimizedCardChannel");
                } catch (err) {
                    console.warn("Failed to remove subscription on cleanup", err);
                }
                subscriptionRef.current = null;
            }
        };
    }, [userId, handleReceivedMinimizedCards]);

    return null;
}

export { MinimizedCardActionCable };